<template>
	<div>
		<div class="flex items-center justify-around sm:justify-start pt-8 sm:pt-16 sm:pb-4 px-24">
			<div v-if="longestTrack?.latlngs[Math.max(selectedIndex, 0)]" class="flex items-center text-sm px-4">
				<Icon :icon="icons.distance" x-small class="mr-4 sm:mr-8"/>
				<span style="width: 70px">{{ displayLatLngs[Math.max(selectedIndex, 0)].distance_from_start < 1000 ? `${n(displayLatLngs[Math.max(selectedIndex, 0)].distance_from_start, {minimumFractionDigits: 2, maximumFractionDigits: 2})} m` : `${n(displayLatLngs[Math.max(selectedIndex, 0)].distance_from_start / 1000, {minimumFractionDigits: 2, maximumFractionDigits: 2})} km` }}</span>
			</div>
			<div v-if="longestTrack?.latlngs[Math.max(selectedIndex, 0)]?.ele" class="flex items-center text-sm px-4">
				<Icon :icon="icons.altitude" x-small class="mr-4 sm:mr-8"/>
				<span style="width: 65px">{{ n(displayLatLngs[Math.max(selectedIndex, 0)].ele, {maximumFractionDigits: 0}) }} m</span>
			</div>
		</div>
		<chart v-model:hover-index="selectedIndex" type="area"
			   :colors="colors"
			   :data="series"
			   :labels="labels"
			   hide-legend
			   disable-animation
			   :x-annotations="xAnnotations"
			   :height="props.height"
			   :options="options"/>

		<div class="flex flex-wrap justify-center w-full mb-16">
			<div v-for="track in props.tracks" :key="track.id"
				 class="flex items-center cursor-pointer px-8 py-2"
				 :class="{'opacity-50': focusedTrack && focusedTrack !== track}"
				 @click="toggleTrackFocus(track)">
				<div class="rounded-full w-10 h-10 mr-4" :style="{backgroundColor: track.color}"></div>
				<span class="text-2xs">{{ track.name }}</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type {Track} from "@spoferan/spoferan-ts-core";
import {ref, watch, computed} from "vue";
import type {Ref, PropType} from "vue";
import {useI18n, useRuntimeConfig} from "#imports";
import {mdiElevationRise, mdiArrowCollapseRight} from "@mdi/js";

const {t, n} = useI18n();
const config = useRuntimeConfig();

const props = withDefaults(defineProps<{
	// The currently hovered x-axis value (distance from start)
	hoverDistance?: number,

	tracks: Track[],

	// The x-axis markers to insert
	markers?: {title: string, x: number, color?: string}[],

	height?: number|string,
}>(), {
	height: 'auto',
	markers: []
});
const emit = defineEmits(['update:hoverDistance']);

const icons = {
	distance: mdiArrowCollapseRight,
	altitude: mdiElevationRise,
}

const focusedTrack: Ref<Track|null> = defineModel('focusTrack', {
	type: Object as PropType<Track>,
	default: null
});
watch(() => props.tracks, () => {
	focusedTrack.value = null;
});
function toggleTrackFocus(track) {
	if (focusedTrack.value === track) {
		focusedTrack.value = null;
	} else {
		focusedTrack.value = track;
	}
}

const displayedTracks: Ref<Track[]> = computed(() => {
	return focusedTrack.value ? [focusedTrack.value as Track] : (props.tracks as Track[]);
});

const longestTrack: Ref<Track|null> = computed(() => {
	if (displayedTracks.value.length === 1) {
		return displayedTracks.value[0];
	}

	return displayedTracks.value.reduce((longestTrack: Track, track: Track) => {
		return !longestTrack || track.latlngs[track.latlngs.length - 1].distance_from_start > longestTrack.latlngs[longestTrack.latlngs.length - 1].distance_from_start ? track : longestTrack;
	}, null);
});

const displayLatLngs = computed(() => {
	if (!longestTrack.value?.latlngs.length) {
		return [];
	}

	// We limit the amount of labels to improve performance.
	// Values above 300 will not render the page with the chart anymore.
	const maxElements = 150;
	const keepNthElement = Math.max(1, Math.floor(longestTrack.value.latlngs.length / maxElements));
	const targetLatLngs = [];
	for (let i = 0; i < longestTrack.value.latlngs.length; i += keepNthElement) {
		targetLatLngs.push(longestTrack.value.latlngs[i]);
	}
	targetLatLngs.push(longestTrack.value.latlngs[longestTrack.value.latlngs.length - 1]);

	return targetLatLngs;
});

const labels = computed(() => {
	return displayLatLngs.value.map(latLng => latLng.distance_from_start);
});

const selectedIndex = ref(-1);
watch(selectedIndex, (newValue) => {
	emit('update:hoverDistance', labels.value[newValue]);
});

const colors = computed(() => {
	return displayedTracks.value.map(track => track.color);
});

// The annotations are only inserted at the correct x position for desktop devices
const xAnnotations = computed(() => {
	return props.markers.map((marker) => {
		return {
			x: marker.x,
			borderColor: marker.color ?? config.public.colors.primary,
			label: {
				show: true,
				text: marker.title,
				borderColor: "#fff",
				style: {
					color: "#fff",
					background: marker.color ?? config.public.colors.primary
				}
			}
		}
	});
});

const series = computed(() => {
	return displayedTracks.value.map(track => {
		return {
			name: track.name,

			// We must iterate over the labels to support displays of different track lengths
			data: labels.value.map((distanceFromStart) => {
				const latLng = track.latlngs.find(latLng => latLng.distance_from_start >= distanceFromStart);
				if (!latLng) {
					return [distanceFromStart, null];
				}

				return [distanceFromStart, latLng.ele.toFixed(1)];
			}),
		};
	});
});

const options = {
	dataLabels: {
		enabled: false
	},
	stroke: {
		width: 3
	},
	xaxis: {
		type: 'numeric',
		position: 'top',
		tickAmount: 5, // Max pretty amount for mobile devices
		axisTicks: {
			show: false
		},
		labels: {
			rotate: 0,
			formatter: function(value) {
				if (value === 0) {
					return t('label.start');
				}
				return `${n((value / 1000), {maximumFractionDigits: 1})} km`;
			}
		},
		tooltip: {
			enabled: false
		}
	},
	yaxis: [
		{
			title: {
				text: `${t('label.altitude')} (m)`,
				offsetX: 2
			},
			// The lowest altitude over all tracks - 50m
			min: () => Math.max(0, displayedTracks.value.reduce((minAltitude: number, track) => Math.min(minAltitude, track.latlngs.reduce((minAltitude: number, latLng) => Math.min(minAltitude, latLng.ele), Number.MAX_SAFE_INTEGER)), Number.MAX_SAFE_INTEGER) - 50),
			forceNiceScale: true,
			tickAmount: 4,
			labels: {
				offsetX: 5,
				align: 'center',
				formatter: (value) => {
					if (!value) {
						return value;
					}

					return n(value);
				}
			}
		}
	],
	tooltip: {
		// We show a custom "tooltip" above the chart
		custom: function() {
			return null
		}
	}
};
</script>
<style >
.apexcharts-tooltip {
  display: none;
}
</style>